import type { ThreadMessage } from '@agentic-flow-studio/shared/components/clinic/ThreadMessageList';
import { buildInfoHeaders } from '@agentic-flow-studio/shared/lib/build-info';
import type { EmailThreadMetadata } from '@agentic-flow-studio/shared/types/email';
import type {
  AccountPermission,
  AnalyticsByInbox,
  AnalyticsByUser,
  AnalyticsOverview,
  AutomationRule,
  CannedResponse,
  CategorySuggestion,
  ClinicAccount,
  ClinicCategory,
  DashboardSummaryItem,
  DomainWeight,
  LabelMapping,
  RuleActionDef,
  RuleConditionDef,
  RuleExecution,
  RuleTriggerType,
  WorkspaceMember,
} from '@/types/clinic';
import { getBetterAuthToken } from './betterAuth';

const BASE_URL = import.meta.env.VITE_WORKER_URL;

async function getHeaders() {
  const workspaceId = localStorage.getItem('workspaceId');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...buildInfoHeaders(),
  };

  const token = await getBetterAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (workspaceId) {
    headers['x-workspace-id'] = workspaceId;
  }

  return headers;
}

function parseJson<T = unknown>(response: Response): Promise<T> {
  return response.json() as Promise<T>;
}

type SearchParamValue = string | number | string[] | undefined;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Skipped when a value is undefined, an empty array, or an empty string. */
  searchParams?: Record<string, SearchParamValue>;
  /** Thrown verbatim on a non-ok response — every call site names its own, matching what it looked like before this was one function. */
  errorMessage: string;
}

/**
 * The one seam every Harbor REST call goes through: headers, query-string
 * building, JSON body, and the ok-check/throw. Every `clinicApi` method below
 * is a one-line call into this — the header/error-shape/query-encoding logic
 * that used to be copy-pasted 30+ times now has exactly one place to fix.
 */
async function request<T = unknown>(path: string, options: RequestOptions): Promise<T> {
  const { method = 'GET', body, searchParams, errorMessage } = options;
  const url = new URL(`${BASE_URL}${path}`);

  for (const [key, value] of Object.entries(searchParams ?? {})) {
    if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
      continue;
    }
    url.searchParams.append(key, Array.isArray(value) ? value.join(',') : String(value));
  }

  const response = await fetch(url.toString(), {
    method,
    headers: await getHeaders(),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) throw new ClinicApiError(errorMessage, response.status);
  return parseJson<T>(response);
}

/**
 * A failed Harbor call, carrying the status the caller needs to tell a settled answer
 * from a transient one.
 *
 * The message stays exactly what the call site named, so every existing toast and error
 * boundary reads the same. The status is additive: without it a 404 -- "you do not have
 * access to this conversation" -- is retried three times with backoff, and a refusal that
 * should be immediate spends seven seconds looking like a slow page.
 */
export class ClinicApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'ClinicApiError';
  }
}

/**
 * React Query's `retry`, for a query whose failure may be an answer.
 *
 * A 4xx is settled: retrying a refusal changes nothing and delays it. A 5xx or a network
 * failure still gets the default three attempts.
 */
export function retryUnlessRefused(failureCount: number, error: unknown): boolean {
  if (error instanceof ClinicApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 3;
}

interface AnalyticsRange {
  from?: number;
  to?: number;
  accountEmails?: string[];
}

export const clinicApi = {
  ingestThread(threadId: string) {
    return request(`/api/threads/ingest`, {
      method: 'POST',
      body: { threadId },
      errorMessage: 'Ingest failed',
    });
  },

  getSummary(accountEmails?: string[]) {
    return request<DashboardSummaryItem[]>(`/api/dashboard/summary`, {
      searchParams: { accountEmails },
      errorMessage: 'Fetch summary failed',
    });
  },

  getAnalyticsOverview(range?: AnalyticsRange) {
    return request<AnalyticsOverview>(`/api/analytics/overview`, {
      searchParams: { from: range?.from, to: range?.to, accountEmails: range?.accountEmails },
      errorMessage: 'Fetch analytics overview failed',
    });
  },

  getAnalyticsByInbox(range?: AnalyticsRange) {
    return request<AnalyticsByInbox[]>(`/api/analytics/by-inbox`, {
      searchParams: { from: range?.from, to: range?.to, accountEmails: range?.accountEmails },
      errorMessage: 'Fetch analytics by inbox failed',
    });
  },

  getAnalyticsByUser(range?: AnalyticsRange) {
    return request<AnalyticsByUser[]>(`/api/analytics/by-user`, {
      searchParams: { from: range?.from, to: range?.to, accountEmails: range?.accountEmails },
      errorMessage: 'Fetch analytics by user failed',
    });
  },

  getThreads(accountEmails?: string[]) {
    return request<EmailThreadMetadata[]>(`/api/threads`, {
      searchParams: { accountEmails },
      errorMessage: 'Fetch threads failed',
    });
  },

  getDomainWeights() {
    return request<DomainWeight[]>(`/api/domains/weights`, {
      errorMessage: 'Fetch domain weights failed',
    });
  },

  updateDomainWeight(domain: string, weight: number) {
    return request<DomainWeight>(`/api/domains/weights`, {
      method: 'POST',
      body: { domain, weight },
      errorMessage: 'Update domain weight failed',
    });
  },

  getCategories() {
    return request<ClinicCategory[]>(`/api/categories`, {
      errorMessage: 'Fetch categories failed',
    });
  },

  updateCategory(category: { id?: number; name: string; color: string; weight: number }) {
    return request<ClinicCategory>(`/api/categories`, {
      method: 'POST',
      body: category,
      errorMessage: 'Update category failed',
    });
  },

  deleteCategory(id: number) {
    return request(`/api/categories/${id}`, {
      method: 'DELETE',
      errorMessage: 'Delete category failed',
    });
  },

  suggestMappings(labels: string[], categories: { id: number; name: string }[]) {
    return request<LabelMapping[]>(`/api/suggest-mappings`, {
      method: 'POST',
      body: { labels, categories },
      errorMessage: 'Suggest mappings failed',
    });
  },

  suggestCategories(labels: string[]) {
    return request<CategorySuggestion[]>(`/api/suggest-categories`, {
      method: 'POST',
      body: { labels },
      errorMessage: 'Suggest categories failed',
    });
  },

  getMailAccounts() {
    return request<ClinicAccount[]>(`/api/mail-accounts`, {
      errorMessage: 'Fetch accounts failed',
    });
  },

  getAccountPermissions(accountId: number) {
    return request<AccountPermission[]>(`/api/accounts/${accountId}/permissions`, {
      errorMessage: 'Fetch permissions failed',
    });
  },

  updateAccountPermissions(accountId: number, userId: number, action: 'add' | 'remove') {
    return request(`/api/accounts/${accountId}/permissions`, {
      method: 'POST',
      body: { userId, action },
      errorMessage: 'Update account permissions failed',
    });
  },

  archiveConversation(conversationId: number) {
    return request(`/api/conversations/${conversationId}/archive`, {
      method: 'POST',
      errorMessage: 'Archive thread failed',
    });
  },

  resolveConversation(conversationId: number, resolved: boolean) {
    return request(`/api/conversations/${conversationId}/resolve`, {
      method: 'PATCH',
      body: { resolved },
      errorMessage: 'Resolve thread failed',
    });
  },

  getWorkspaceMembers() {
    return request<WorkspaceMember[]>(`/api/workspace/members`, {
      errorMessage: 'Fetch members failed',
    });
  },

  getConversation(conversationId: number) {
    return request<EmailThreadMetadata>(`/api/conversations/${conversationId}`, {
      errorMessage: 'Fetch thread failed',
    });
  },

  getConversationMessages(conversationId: number) {
    // `source` says whether this came from the provider or from retained evidence. The
    // renderer needs to know: a retained timeline carries no bodies, only the ids to ask
    // for them with.
    return request<{ messages: ThreadMessage[]; source?: 'provider' | 'retained' }>(
      `/api/conversations/${conversationId}/messages`,
      {
        errorMessage: 'Fetch thread messages failed',
      }
    );
  },

  getRules() {
    return request<AutomationRule[]>(`/api/rules`, {
      errorMessage: 'Fetch rules failed',
    });
  },

  createRule(rule: {
    name: string;
    description?: string;
    triggerType: RuleTriggerType;
    conditions: RuleConditionDef[];
    actions: RuleActionDef[];
    priority?: number;
    stopOnMatch?: boolean;
    isEnabled?: boolean;
  }) {
    return request<AutomationRule>(`/api/rules`, {
      method: 'POST',
      body: rule,
      errorMessage: 'Create rule failed',
    });
  },

  updateRule(
    id: number,
    rule: Partial<{
      name: string;
      description: string;
      triggerType: RuleTriggerType;
      conditions: RuleConditionDef[];
      actions: RuleActionDef[];
      priority: number;
      stopOnMatch: boolean;
      isEnabled: boolean;
    }>
  ) {
    return request<AutomationRule>(`/api/rules/${id}`, {
      method: 'PATCH',
      body: rule,
      errorMessage: 'Update rule failed',
    });
  },

  toggleRule(id: number, isEnabled: boolean) {
    return request<AutomationRule>(`/api/rules/${id}/toggle`, {
      method: 'POST',
      body: { isEnabled },
      errorMessage: 'Toggle rule failed',
    });
  },

  deleteRule(id: number) {
    return request(`/api/rules/${id}`, {
      method: 'DELETE',
      errorMessage: 'Delete rule failed',
    });
  },

  getRuleExecutions(ruleId: number) {
    return request<RuleExecution[]>(`/api/rules/${ruleId}/executions`, {
      errorMessage: 'Fetch rule executions failed',
    });
  },

  getCannedResponses() {
    return request<CannedResponse[]>(`/api/canned-responses`, {
      errorMessage: 'Fetch canned responses failed',
    });
  },

  createCannedResponse(data: { name: string; subjectTemplate?: string; bodyTemplate: string }) {
    return request<CannedResponse>(`/api/canned-responses`, {
      method: 'POST',
      body: data,
      errorMessage: 'Create canned response failed',
    });
  },

  updateCannedResponse(
    id: number,
    data: Partial<{ name: string; subjectTemplate: string; bodyTemplate: string }>
  ) {
    return request<CannedResponse>(`/api/canned-responses/${id}`, {
      method: 'PATCH',
      body: data,
      errorMessage: 'Update canned response failed',
    });
  },

  deleteCannedResponse(id: number) {
    return request(`/api/canned-responses/${id}`, {
      method: 'DELETE',
      errorMessage: 'Delete canned response failed',
    });
  },
};
